import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type {
  Agent,
  AgentDeleteResult,
  AgentTeamEntry,
  AgentWorkspaceEntry,
  CreateAgentRequest,
  Session,
  UpdateAgentRequest,
} from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

export const AGENTS_KEY = ['agents'] as const;

function useAgentQueriesEnabled(): boolean {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return Boolean(isAuthenticated && sdkConfigured);
}

function invalidateAgents(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [...AGENTS_KEY] });
}

export function useAgents() {
  const enabled = useAgentQueriesEnabled();
  return useQuery<Agent[], Error>({
    queryKey: [...AGENTS_KEY, 'list'],
    queryFn: () => getSDK().agents.list(),
    enabled,
  });
}

export function useAgent(agentId: string | undefined) {
  const enabled = useAgentQueriesEnabled();
  return useQuery<Agent, Error>({
    queryKey: [...AGENTS_KEY, 'detail', agentId],
    queryFn: () => {
      if (!agentId) throw new Error('useAgent queried without an agent id');
      return getSDK().agents.get(agentId);
    },
    enabled: enabled && Boolean(agentId),
  });
}

export function useAgentWorkspaces(agentId: string | undefined) {
  const enabled = useAgentQueriesEnabled();
  return useQuery<AgentWorkspaceEntry[], Error>({
    queryKey: [...AGENTS_KEY, 'workspaces', agentId],
    queryFn: () => {
      if (!agentId) throw new Error('useAgentWorkspaces queried without an agent id');
      return getSDK().agents.workspaces(agentId);
    },
    enabled: enabled && Boolean(agentId),
    staleTime: 0,
  });
}

export function useAgentTeams(agentId: string | undefined) {
  const enabled = useAgentQueriesEnabled();
  return useQuery<AgentTeamEntry[], Error>({
    queryKey: [...AGENTS_KEY, 'teams', agentId],
    queryFn: () => {
      if (!agentId) throw new Error('useAgentTeams queried without an agent id');
      return getSDK().agents.teams(agentId);
    },
    enabled: enabled && Boolean(agentId),
    staleTime: 0,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();
  return useMutation<Agent, Error, CreateAgentRequest>({
    mutationFn: (params) => getSDK().agents.create(params),
    onSuccess: () => invalidateAgents(queryClient),
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();
  return useMutation<Agent, Error, { agentId: string } & UpdateAgentRequest>({
    mutationFn: ({ agentId, ...params }) => getSDK().agents.update(agentId, params),
    onSuccess: () => invalidateAgents(queryClient),
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();
  return useMutation<AgentDeleteResult, Error, { agentId: string }>({
    mutationFn: ({ agentId }) => getSDK().agents.delete(agentId),
    onSuccess: () => {
      invalidateAgents(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['session-list-pages'] });
    },
  });
}

export function useStartAgentSession() {
  const queryClient = useQueryClient();
  return useMutation<Session, Error, { agentId: string }>({
    mutationFn: ({ agentId }) => getSDK().agents.startSession(agentId),
    onSuccess: () => {
      invalidateAgents(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['session-list-pages'] });
    },
  });
}
